import { clone } from '@jack-henry/frontend-utils/functions';
import { Nullable } from '@jack-henry/frontend-utils/types';
import {
    CompanyAccountModelDto,
    InternalTransferAddTransactionModelDto,
    InternalTransferTransactionModelDto,
    FrequencyModelDto,
} from '@treasury/api/channel';
import { format } from 'date-fns';
import { CompanyAccount } from '../company/company-account.entity';
import { DomainEntity } from '../shared';
import { LoanDto, nullLoan } from './loan.dto';

export class Loan extends DomainEntity<Nullable<LoanDto>> {
    constructor(dto = clone(nullLoan)) {
        super(dto);
    }

    public createDefault(): this {
        return new Loan(this.dto) as this;
    }

    public get transactionId() {
        return this.dto.transactionId || '';
    }

    public set transactionId(transactionId) {
        this.dto.transactionId = transactionId;
    }

    public get lastEditedDate() {
        return this.dto.lastEditedDate;
    }

    public set lastEditedDate(lastEditedDate) {
        this.dto.lastEditedDate = lastEditedDate;
    }

    public get isEdited() {
        return this.dto.isEdited || false;
    }

    public get transferDateFormatted() {
        if (this.dto.transferDate) {
            return format(new Date(this.dto.transferDate || ''), 'MM/dd/yyyy');
        }

        return undefined;
    }

    public get transferDate() {
        return this.dto.transferDate || '';
    }

    public set transferDate(transferDate) {
        this.dto.transferDate = transferDate;
    }

    public get amount() {
        return this.dto.amount || 0;
    }

    public set amount(amount) {
        this.dto.amount = amount;
    }

    public get amountFormatted() {
        return this.amount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        });
    }

    public get memo() {
        return this.dto.memo || '';
    }

    public set memo(memo) {
        this.dto.memo = memo;
    }

    public get status() {
        return this.dto.status || '';
    }

    public set status(status) {
        this.dto.status = status;
    }

    public get statusFormatted() {
        return this.dto.status === 'PendingApproval' ? 'Pending Approval' : this.dto.status;
    }

    public get comments() {
        return this.dto.comments || '';
    }

    public set comments(comments) {
        this.dto.comments = comments;
    }

    public get updatedBy() {
        return this.dto.updatedBy || '';
    }

    public set updatedBy(updatedBy) {
        this.dto.updatedBy = updatedBy;
    }

    public get frequency() {
        return this.dto.frequency || '';
    }

    public get fromAccount() {
        return this.dto.fromAccount as unknown as CompanyAccount;
    }

    public set fromAccount(fromAccount) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.dto.fromAccount = fromAccount as unknown as any;
    }

    public get toAccount() {
        return this.dto.toAccount as unknown as CompanyAccount;
    }

    public set toAccount(toAccount) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.dto.toAccount = toAccount as unknown as any;
    }

    public get paymentOptionType() {
        if (this.dto.paymentOptionType?.toString() === '2') {
            return 'Principal Only';
        } else if (this.dto.paymentOptionType?.toString() === '1') {
            return 'Regular Payment';
        }
        return this.dto.paymentOptionType || '';
    }

    public set paymentOptionType(paymentOptionType) {
        this.dto.paymentOptionType = paymentOptionType;
    }

    public get batchId() {
        return this.dto.batchId || '';
    }

    public set batchId(batchId) {
        this.dto.batchId = batchId;
    }

    public get id() {
        return this.dto.id;
    }

    public get completedApprovals() {
        return this.dto.completedApprovals || 0;
    }

    public get completedApprovalCount() {
        return this.dto.completedApprovalCount || 0;
    }

    public get availableApprovers() {
        return this.dto.availableApprovers || 0;
    }

    public get numberOfApprovalsNeeded() {
        return this.dto.numberOfApprovalsNeeded || 0;
    }

    public get audit() {
        return this.dto.audit || [];
    }

    public get createdDate() {
        return this.dto.createdDate || new Date();
    }

    public get createdDateFormatted() {
        return format(new Date(this.createdDate), 'MM/dd/yyyy');
    }

    public get permissions() {
        return this.dto.permissions ? this.dto.permissions.map(p => p.permission) : [];
    }

    public toCreateDto(): InternalTransferAddTransactionModelDto {
        return {
            transactions: [
                {
                    transactionId: this.transactionId,
                    amount: this.amount,
                    memo: this.memo,
                    fromAccount: this.fromAccount.toDto() as unknown as CompanyAccountModelDto,
                    toAccount: this.toAccount?.toDto() as unknown as CompanyAccountModelDto,
                    paymentOptionType: this.paymentOptionType === 'Principal Only' ? 2 : 1,
                    transferDate: this.transferDate as string | undefined,
                    id: 0,
                    updatedDate: '',
                },
            ],
            type: 'one',
            id: 0,
            updatedDate: '',
        };
    }

    public toUpdateDto(): InternalTransferTransactionModelDto {
        return {
            transactionId: this.transactionId,
            amount: this.amount,
            memo: this.memo,
            fromAccount: this.fromAccount.toDto() as unknown as CompanyAccountModelDto,
            toAccount: this.toAccount?.toDto() as unknown as CompanyAccountModelDto,
            paymentOptionType: this.paymentOptionType === 'Principal Only' ? '2' : '1',
            transferDate: this.transferDate,
            status: this.status || '',
            numberOfApprovalsNeeded: this.numberOfApprovalsNeeded || 0,
            isEdited: false,
            createdDate: this.createdDate as unknown as string,
            id: this.id || 0,
            updatedDate: '',
            batchId: this.batchId,
            audit: this.audit,
            updatedBy: this.updatedBy,
            lastEditedDate: this.lastEditedDate as unknown as string,
            message: '',
            messageType: '',
            frequencyDescription: this.dto.frequencyDescription as string,
            frequency: this.dto.frequency as unknown as string,
            frequencyModel:
                // eslint-disable-next-line no-restricted-syntax
                this.dto.frequencyModel === null
                    ? undefined
                    : (this.dto.frequencyModel as unknown as FrequencyModelDto),
            parentFrequencyModel:
                // eslint-disable-next-line no-restricted-syntax
                this.dto.parentFrequencyModel === null
                    ? undefined
                    : (this.dto.parentFrequencyModel as unknown as FrequencyModelDto),
            completedApprovals: [],
            availableApprovers: [],
            completedApprovalCount: this.completedApprovalCount,
        };
    }
}
